import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "./EmblaCarouselArrowButtons";
import "../styles/galleryembla.css";
const TWEEN_FACTOR_BASE = 0.84;

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max);

const InvestPropertyGalleryCarousel = ({
  slides,
  options,
  navClass,
  setIndex,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const tweenFactor = useRef(0);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);
  useEffect(() => {
    setIndex(selectedIndex);
  }, [selectedIndex]);
  const setTweenFactor = useCallback((emblaApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenOpacity = useCallback((emblaApi, eventName) => {
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === "scroll";

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const opacity = numberWithinRange(tweenValue, 0, 1).toString();
        emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setTotalSlides(emblaApi.scrollSnapList().length);

    emblaApi
      .on("reInit", () => {
        setTweenFactor(emblaApi);
        tweenOpacity(emblaApi);
        setTotalSlides(emblaApi.scrollSnapList().length);
      })
      .on("scroll", () => tweenOpacity(emblaApi, "scroll"))
      .on("select", () => setSelectedIndex(emblaApi.selectedScrollSnap()));
  }, [emblaApi, tweenOpacity]);

  return (
    <div className="embla flex flex-col items-center w-full">
      <div className="embla__viewport w-full" ref={emblaRef}>
        <div className="embla__container flex">
          {slides.map((slide, index) => (
            <div className="embla__slide flex-[0_0_100%]" key={index}>
              <img
                className="embla__slide__img w-full object-contain"
                src={slide}
                alt={`Slide ${index + 1}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Controls aligned to the right */}
      <div className="w-full px-4 mt-4">
        {/* Title aligned with nav + counter */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white text-2xl font-semibold mb-2">
              Built for the city slicker
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm">
              {selectedIndex + 1} / {totalSlides}
            </span>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              navClass={navClass}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              navClass={navClass}
            />
          </div>
        </div>

        {/* Paragraph below */}
        <p className="text-white/90 text-sm mt-4 max-w-xl text-left">
          This is the epitome of modern urban living in the heart of Houghton,
          Johannesburg. Sleek and stylish, this apartment complex offers the
          ultimate blend of luxury and convenience, designed to elevate the city
          lifestyle.
        </p>
      </div>
    </div>
  );
};

export default InvestPropertyGalleryCarousel;
